const ASSET_REGEX = /(https?:\/\/[^\s]+\.(?:png|jpe?g|webp|gif|svg|avif|mp4|webm|mov|m4v)(?:\?.*)?$)|(^\/?[^\s]+\.(?:png|jpe?g|webp|gif|svg|avif|mp4|webm|mov|m4v)(?:\?.*)?$)/i;

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v']);

const getExtension = (url: string): string | undefined => {
  const sanitized = url.split('?')[0]?.trim();
  if (!sanitized) return undefined;
  const segments = sanitized.split('.');
  return segments.length > 1 ? segments.pop()?.toLowerCase() : undefined;
};

type Priority = 'high' | 'background';

type QueueItem = {
  url: string;
  priority: Priority;
};

const loadedUrls = new Set<string>();
const loadingUrls = new Set<string>();
const highPriorityQueue: QueueItem[] = [];
const backgroundQueue: QueueItem[] = [];
let isProcessing = false;

const shouldPreload = (value: string | undefined | null): value is string => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('data:')) return false;
  return ASSET_REGEX.test(trimmed);
};

const loadAsset = (url: string): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    const extension = getExtension(url);

    if (extension && VIDEO_EXTENSIONS.has(extension)) {
      const video = document.createElement('video');
      const finish = () => {
        video.removeAttribute('src');
        video.load();
        resolve();
      };

      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('loadeddata', finish, { once: true });
      video.addEventListener('error', finish, { once: true });
      video.src = url;
      return;
    }

    const img = new window.Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
};

const getNextItem = (): QueueItem | undefined => {
  if (highPriorityQueue.length > 0) {
    return highPriorityQueue.shift();
  }
  if (backgroundQueue.length > 0) {
    return backgroundQueue.shift();
  }
  return undefined;
};

const scheduleProcess = () => {
  if (isProcessing) return;
  if (typeof window === 'undefined') return;

  const item = getNextItem();
  if (!item) return;

  isProcessing = true;

  const startLoad = () => {
    loadingUrls.add(item.url);

    loadAsset(item.url)
      .catch(() => undefined)
      .finally(() => {
        loadingUrls.delete(item.url);
        loadedUrls.add(item.url);
        isProcessing = false;
        scheduleProcess();
      });
  };

  if (item.priority === 'background') {
    const idleInvoker = (window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    }).requestIdleCallback;

    if (idleInvoker) {
      idleInvoker(() => startLoad(), { timeout: 400 });
    } else {
      window.setTimeout(startLoad, 100);
    }
  } else {
    startLoad();
  }
};

const enqueue = (url: string, priority: Priority) => {
  if (loadedUrls.has(url) || loadingUrls.has(url)) {
    return;
  }

  const queue = priority === 'high' ? highPriorityQueue : backgroundQueue;
  const alreadyQueued = queue.some((item) => item.url === url);

  if (!alreadyQueued) {
    queue.push({ url, priority });
  }
};

export const preloadImages = (urls: string[], priority: Priority = 'background') => {
  if (typeof window === 'undefined') return;

  urls.forEach((url) => {
    if (!shouldPreload(url)) return;
    enqueue(url.trim(), priority);
  });

  scheduleProcess();
};

export const markImagesAsCritical = (urls: string[]) => {
  preloadImages(urls, 'high');
};

export const isImageLoaded = (url: string): boolean => {
  return loadedUrls.has(url);
};

export const getPreloadedImages = (): string[] => {
  return Array.from(loadedUrls);
};

export const getPendingQueueSize = (): number => {
  return highPriorityQueue.length + backgroundQueue.length + loadingUrls.size;
};

export const resetImagePreloader = () => {
  highPriorityQueue.length = 0;
  backgroundQueue.length = 0;
  loadingUrls.clear();
  loadedUrls.clear();
  isProcessing = false;
};
